import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
import useDebounce from "Components/common/hooks/useDebounce";
import CheckBox from "Components/common/CheckBox";
import Button from "Components/common/Button";
import SearchInputBox from "Components/common/SearchInputBox";
import { verifyBulkActionPerformedAndAllow } from "../DataSearchUtils";
import { dataSearchState, getIndustriesCatalog, getTechnologiesList, industryLabel } from "../state";

const AutoCompleteMultiSelectDropdown = observer(props => {
  const {
    id = "",
    name = "",
    showSearchPanel = true,
    setTableLoading = () => { },
    setActiveIndex = () => { },
    showApplybtn = false,
    discoverPage = false,
    disableOverlayClick = false
  } = props || {};

  const searchBoxRef = useRef(null);
  const [searchValue, setSearchValue] = useState(null);
  const [searchLoader, setSearchLoader] = useState(true);

  // Sync search input to global state so parent can hide field hint when user is typing / results appear
  const handleSearchValueChange = val => {
    setSearchValue(val);
    const q = val != null ? String(val).trim() : "";
    dataSearchState.setFilterPanelSearchQuery({ [id]: q });
  };
  const debouncedValue = useDebounce(searchValue, 600);
  const [filteredList, setFilteredList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [clearSearchInput, setClearSearchInput] = useState(false);
  const [technologiesList, setTechnologiesList] = useState([]);
  const [topIndustries, setTopIndustries] = useState([]);

  // Build "all selected at top, then default list (no duplicates)" for industry/technologies
  const buildSelectedFirstThenDefault = (defaultItems, idKey = "id") => {
    const alreadySelected = dataSearchState?.selectedFilteredList?.[id] || [];
    const selectedIds = new Set(
      alreadySelected.map(i => i?.id ?? i?.[idKey] ?? i?.name).filter(Boolean)
    );
    const selectedItems = alreadySelected.map(item => ({
      ...item,
      id: item?.id ?? item?.name,
      name: item?.name ?? item?.id ?? "",
      checked: true
    }));
    const defaultList = defaultItems.map(item => ({
      ...item,
      checked: selectedIds.has(item?.id ?? item?.name)
    }));
    const rest = defaultList.filter(
      i => !selectedIds.has(i?.id) && !selectedIds.has(i?.name)
    );
    return [...selectedItems, ...rest];
  };

  const rebuildIndustryList = (list = topIndustries) => {
    const defaultIndustries = (list || []).map(item => ({
      id: item,
      name: item.replace(/\b\w/g, l => l.toUpperCase()),
      checked: false
    }));
    setFilteredList(buildSelectedFirstThenDefault(defaultIndustries));
  };

  const rebuildTechnologiesList = (list = technologiesList) => {
    const excludeFromDisplay = new Set(["mobile friendly"]);
    const defaultTechnologies = (list || [])
      .filter(item => !excludeFromDisplay.has(String(item || "").trim().toLowerCase()))
      .slice(0, 60)
      .map(item => ({
        id: item,
        name: item.replace(/\b\w/g, l => l.toUpperCase()),
        checked: false
      }));
    setFilteredList(buildSelectedFirstThenDefault(defaultTechnologies));
  };

  // Load technologies list from public/ at runtime (avoids bundling ~300KB in build)
  useEffect(() => {
    if (id?.toLowerCase() !== "technologies" || !props.showDropdown) return;
    let cancelled = false;
    getTechnologiesList().then(list => {
      if (!cancelled && list?.length) setTechnologiesList(list);
    });
    return () => { cancelled = true; };
  }, [id, props.showDropdown]);

  useEffect(() => {
    const isIndustryDropdown = id?.toLowerCase() === "industry" || id?.toLowerCase() === "industryexclude";
    if (!isIndustryDropdown || !props.showDropdown) return;
    let cancelled = false;
    getIndustriesCatalog().then(catalog => {
      if (!cancelled) {
        setTopIndustries(catalog?.topIndustries || []);
      }
    });
    return () => { cancelled = true; };
  }, [id, props.showDropdown]);

  const isIndustryLike = id?.toLowerCase() === "industry" || id?.toLowerCase() === "industryexclude";

  // Load top industries or technologies as default when accordion opens
  useEffect(() => {
    if (isIndustryLike && props.showDropdown) {
      rebuildIndustryList(topIndustries);
      setShowDropdown(true);
    } else if (id?.toLowerCase() === "technologies" && props.showDropdown) {
      setShowDropdown(true);
      rebuildTechnologiesList(technologiesList);
    } else if (!props.showDropdown) {
      setShowDropdown(false);
      setFilteredList([]);
      setSearchValue(null);
      dataSearchState.setFilterPanelSearchQuery({ [id]: "" });
    }
  }, [props.showDropdown, technologiesList, topIndustries]);

  // When store selection is cleared from outside (e.g. slider "Clear"), sync dropdown list so checkboxes reflect it
  const selectedLength = dataSearchState?.selectedFilteredList?.[id]?.length ?? 0;

  // Sync checked state in current list without resetting to defaults (used when search is active)
  const syncCheckedState = () => {
    setFilteredList(prev => {
      const alreadySelected = dataSearchState?.selectedFilteredList?.[id] || [];
      const selectedIds = new Set(alreadySelected.map(i => i?.id ?? i?.name).filter(Boolean));
      return (prev || []).map(item => ({
        ...item,
        checked: selectedIds.has(item?.id) || selectedIds.has(item?.name)
      }));
    });
  };

  useEffect(() => {
    if (!props.showDropdown) return;
    if (isIndustryLike) {
      // If user has an active search query, keep the current results and just sync checked state
      if (searchValue) {
        syncCheckedState();
      } else {
        rebuildIndustryList(topIndustries);
      }
    } else if (id?.toLowerCase() === "technologies") {
      if (searchValue) {
        syncCheckedState();
      } else {
        rebuildTechnologiesList();
      }
    } else if (selectedLength === 0 && filteredList?.length > 0) {
      setFilteredList(prev => (prev || []).map(item => ({ ...item, checked: false })));
    }
  }, [selectedLength]);

  const mergeById = (firstArr = [], secondArr = []) =>
    firstArr?.length > 0 &&
    firstArr.map(firstItem => {
      const updatedSecondItem = secondArr.find(
        secondItem => secondItem.id === firstItem.id && secondItem
      ) || [...secondArr];
      return {
        ...updatedSecondItem,
        ...firstItem
      };
    });

  const mergeFilterList = data => {
    const updatedAlreadySelectedFilteredList =
      (dataSearchState?.selectedFilteredList?.[id]?.length > 0 &&
        dataSearchState?.selectedFilteredList?.[id]) ||
      [];
    var mergedObj = mergeById(data, updatedAlreadySelectedFilteredList);
    return mergedObj;
  };

  // Build list with selected items always at the top, then search results (excluding already selected)
  const selectedFirstThenSearchResults = (searchResults = [], selectedList = []) => {
    const selectedIds = new Set((selectedList || []).map(i => i?.id ?? i?.name).filter(Boolean));
    const selectedItems = (selectedList || []).map(item => ({
      ...item,
      id: item?.id ?? item?.name,
      name: item?.name ?? item?.id ?? "",
      checked: true
    }));
    const rest = (searchResults || []).filter(
      item => !selectedIds.has(item?.id) && !selectedIds.has(item?.name)
    );
    const mergedRest = selectedIds.size > 0 ? mergeFilterList(rest) : rest;
    return [...selectedItems, ...(Array.isArray(mergedRest) ? mergedRest : [])];
  };

  const updateKeywordResponse = (tempResponse = []) => {
    if (tempResponse?.length > 0) {
      return tempResponse.map(item => ({
        name: item,
        id: item,
        group: "keywords",
        searchText: [item]
      }));
    }
  };

  // Location API (data-core/Solr) returns { id, name, group, searchText }
  // Normalize to { id, name, type, group, searchText } so payload building and display work
  const updateLocationResponse = (tempResponse = []) => {
    if (!Array.isArray(tempResponse) || tempResponse.length === 0) return tempResponse;
    return tempResponse.map(item => {
      const group = (item?.group || "city").toLowerCase();
      const name = item?.name ?? "";
      const typeMap = { country: "Country", state: "State", city: "City" };
      return {
        ...item,
        id: item?.id ?? name,
        name,
        type: typeMap[group] || "City",
        group,
        searchText: Array.isArray(item?.searchText) ? item.searchText : [name]
      };
    });
  };

  const searchFilterList = async () => {
    setSearchLoader(true);
    setSearchValue(debouncedValue);
    const performAction = async () => {
      setShowDropdown(true);
      setClearSearchInput(false);
      const lowercasedValue = debouncedValue?.toLowerCase();
      const { data: responseData = [] } =
        await dataSearchState.searchFilterWithAPI(lowercasedValue, id);
      const isKeywordId = ["keywords", "companykeywords", "contactkeywords"].includes(id?.toLowerCase());
      const tempResponseData =
        isKeywordId
          ? updateKeywordResponse(responseData)
          : id?.toLowerCase() === "location" || id?.toLowerCase() === "companylocation"
            ? updateLocationResponse(responseData)
            : responseData;
      // When searching: show search results at top (order = API order), with selected state merged
      const updatedMergeList =
        dataSearchState?.selectedFilteredList?.[id]?.length > 0
          ? mergeFilterList(tempResponseData)
          : tempResponseData;
      setFilteredList(updatedMergeList);
      setSearchLoader(false);
    };
    if (["bulk", "custom"].includes(dataSearchState?.rangeSelected?.type)) {
      setClearSearchInput(false);
      verifyBulkActionPerformedAndAllow(performAction, () =>
        setClearSearchInput(true)
      );
    } else {
      performAction();
    }
  };

  const handleSelectAction = (checked = "", uId = "") => {
    let tempData = [...filteredList];
    tempData = tempData.map(item => {
      const { id = "" } = item || {};
      if (id === uId) {
        item.checked = checked;
      }
      return item;
    });
    setFilteredList(tempData);
    applySelectionState(tempData);
  };

  const handleAddCustomItem = () => {
    let tempFilteredList = [
      ...[...(filteredList || [])],
      {
        checked: true,
        id: searchValue,
        name: searchValue,
        group: "keywords",
        searchText: [searchValue]
      }
    ];
    setFilteredList(tempFilteredList);
    handleApplySelectionButton(tempFilteredList);
  };

  const applySelectionState = (tempFilteredList = []) => {
    let tempData = tempFilteredList?.length
      ? tempFilteredList
      : [...filteredList];
    const updatedFilteredData = tempData?.filter(item => item?.checked);
    const updatedAlreadySelectedFilteredList =
      (dataSearchState?.selectedFilteredList?.[id]?.length > 0 &&
        dataSearchState?.selectedFilteredList?.[id]) ||
      [];

    const checkedForChanges = (filteredList = [], newData = []) => {
      let result = filteredList.filter(o1 => {
        return newData.some(o2 => {
          return o1?.id === o2?.id; // return the ones with equal id
        });
      });
      return result;
    };

    const getMergedData = (arr1 = [], arr2 = []) => {
      const mergedData = arr1.map(data => ({
        ...data,
        ...arr2.find(newData => newData?.id === data?.id)
      }));
      return mergedData;
    };

    const updatedNewData = checkedForChanges(
      tempFilteredList?.length ? tempFilteredList : filteredList,
      updatedAlreadySelectedFilteredList
    );

    const mergedData = getMergedData(
      updatedAlreadySelectedFilteredList,
      updatedNewData
    );

    const updatedSelectedFilteredList =
      updatedAlreadySelectedFilteredList?.length > 0
        ? [...updatedFilteredData, ...mergedData]
        : [...updatedFilteredData];

    const newData = [
      ...new Set(updatedSelectedFilteredList.map(d => d?.id))
    ].map(id => {
      return {
        ...updatedSelectedFilteredList.filter(d => d?.id === id)?.[0]
      };
    });
    // For industry include/exclude: when an item is added to one side, remove it from the other
    const oppositeKey = id === "industry" ? "industryExclude" : id === "industryExclude" ? "industry" : null;
    const newCheckedIds = new Set(newData.filter(i => i?.checked).map(i => i?.id ?? i?.name).filter(Boolean));
    let updatedSl = { ...dataSearchState.selectedFilteredList };
    if (oppositeKey) {
      updatedSl[oppositeKey] = (updatedSl[oppositeKey] || []).filter(i => {
        const iid = typeof i === "object" ? (i?.id ?? i?.name) : i;
        return !newCheckedIds.has(iid);
      });
    }
    updatedSl[id] = [...newData?.filter(item => item?.checked)];
    dataSearchState.setSelectedFilteredList(updatedSl);

    const updatedFilteredList = newData
      ?.map(item => {
        if (item.checked) {
          if (id === "companyDomain") return item?.primaryDomain;
          if (id === "industry" || id === "industryExclude") return item?.name || item?.id;
          if (id === "technologies") return item?.id;
          return { [item?.group]: item?.searchText };
        }
      })
      ?.filter(Boolean);

    if (id === "companyDomain" || id === "industry" || id === "industryExclude" || id === "technologies") {
      const fdUpdate = { ...dataSearchState.filterComponentData, [id]: updatedFilteredList };
      // Mirror the cross-removal into filterComponentData
      if (oppositeKey) {
        fdUpdate[oppositeKey] = (dataSearchState.filterComponentData[oppositeKey] || []).filter(
          name => !newCheckedIds.has(name)
        );
      }
      dataSearchState.setFilterComponentData(fdUpdate);
    } else if (id?.toLowerCase() === "location") {
      // Handle contact location - create country/state/city structure
      const country = [];
      const state = [];
      const city = [];
      updatedFilteredList.forEach(item => {
        if (!item || typeof item !== 'object') return;
        const keys = Object.keys(item);
        if (keys.length === 0) return;
        const key = keys[0];
        const value = item[key];
        if (Array.isArray(value)) {
          if (key === "state") {
            state.push(...value);
          } else if (key === "country") {
            country.push(...value);
          } else {
            city.push(...value);
          }
        } else if (value != null) {
          // Handle single value (not an array)
          if (key === "state") {
            state.push(value);
          } else if (key === "country") {
            country.push(value);
          } else {
            city.push(value);
          }
        }
      });
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        country: country.filter(Boolean),
        state: state.filter(Boolean),
        city: city.filter(Boolean)
      });
    } else if (id?.toLowerCase() === "companylocation") {
      // Handle company location - create companyCountry/companyState/companyCity structure
      const companyCountry = [];
      const companyState = [];
      const companyCity = [];
      updatedFilteredList.forEach(item => {
        if (!item || typeof item !== 'object') return;
        const keys = Object.keys(item);
        if (keys.length === 0) return;
        const key = keys[0];
        const value = item[key];
        if (Array.isArray(value)) {
          if (key === "state") {
            companyState.push(...value);
          } else if (key === "country") {
            companyCountry.push(...value);
          } else {
            companyCity.push(...value);
          }
        } else if (value != null) {
          // Handle single value (not an array)
          if (key === "state") {
            companyState.push(value);
          } else if (key === "country") {
            companyCountry.push(value);
          } else {
            companyCity.push(value);
          }
        }
      });
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        companyCountry: companyCountry.filter(Boolean),
        companyState: companyState.filter(Boolean),
        companyCity: companyCity.filter(Boolean)
      });
    } else if (["companykeywords", "contactkeywords", "companykeywordsexclude", "contactkeywordsexclude"].includes(id?.toLowerCase())) {
      const keywordValues = (updatedFilteredList || [])
        .map(item => {
          const key = Object.keys(item || {})?.[0];
          const val = item?.[key];
          return Array.isArray(val) ? val : val != null ? [val] : [];
        })
        .reduce((acc, arr) => acc.concat(arr), [])
        .filter(Boolean);
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        [id]: keywordValues
      });
    } else {
      const finalList = updatedFilteredList.reduce((acc, curr) => {
        const key = Object.keys(curr)?.[0];
        const found = acc.find(i => i?.[key]);
        if (!found) {
          acc.push(curr);
        } else {
          found[key] = [...found[key], ...curr[key]];
        }
        return acc;
      }, []);
      dataSearchState.setFilterComponentData({
        ...dataSearchState.filterComponentData,
        ...Object.assign({}, ...finalList)
      });
    }
  };

  const handleApplySelectionButton = (tempFilteredList = []) => {
    setShowDropdown(false);
    setClearSearchInput(true);
    applySelectionState(tempFilteredList);
  };

  const resetState = () => {
    setShowDropdown(false);
    setClearSearchInput(true);
    setFilteredList([]);
    setActiveIndex("");
    setSearchValue("");
    dataSearchState.setFilterPanelSearchQuery({ [id]: "" });
  };

  const controlApplyBtnVisiblity = () => {
    return false; // hide apply button
  };

  const checkApplyCustomBtn = () => {
    return !filteredList?.length > 0 &&
      searchValue &&
      ["keywords", "companykeywords", "contactkeywords"].includes(id?.toLowerCase())
      ? "customBtn"
      : "";
  };

  const triggerSearchUsingDebounce = () => {
      if (!debouncedValue) {
      if (isIndustryLike && props.showDropdown) {
        rebuildIndustryList(topIndustries);
        setShowDropdown(true);
        return;
      }
      if (id?.toLowerCase() === "technologies" && props.showDropdown) {
        rebuildTechnologiesList(technologiesList);
        setShowDropdown(true);
        return;
      }
      setShowDropdown(false);
    }
    if (searchValue !== null) {
      searchFilterList();
    }
  };

  useEffect(() => {
    triggerSearchUsingDebounce();
  }, [debouncedValue]);

  const canShowAddCustomItem =
    debouncedValue &&
    !searchLoader &&
    ["keywords", "companykeywords", "contactkeywords"].includes(
      id?.toLowerCase()
    );

  const shouldShowList =
    (filteredList?.length || 0) > 0 || canShowAddCustomItem;

  return (
    <>
      <span className="horizontalBorderLine" />
      <div className="autoCompleteDropdownListWrapper">
        {showSearchPanel && (
          <div className="searchInputBoxWrap filter-panel-search-wrap">
            <SearchInputBox
              valueCbk={handleSearchValueChange}
              type="text"
              placeholder={`Search ${name}`}
              searchBoxRef={searchBoxRef}
              clearSearchInput={clearSearchInput}
              searchIconAlign={"right"}
              hasCloseIcon={false}
              discoverPage={discoverPage}
            />
          </div>
        )}
        {showDropdown && (
          <>
            {shouldShowList && (
              <div
                className={`dropdownListWrapper ${id} ${checkApplyCustomBtn()}`}
              >
                <div className="dropdownList">
                  {filteredList?.length > 0
                    ? filteredList?.map((item, index) => {
                    const {
                      id: itemId = "",
                      name = "",
                      checked = false,
                      primaryDomain = ""
                    } = item || {};
                    return (
                      <div className="dropdownItems" key={itemId + index}>
                        <CheckBox
                          key={itemId}
                          name={
                            id === "companyDomain" ? (
                              <div className="companyDomainWrapper">
                                <span className="companyName">{name}</span>
                                <span className="primaryDomain">
                                  {primaryDomain}
                                </span>
                              </div>
                            ) : id === "location" ? (
                              <div className="locationWrapper" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <span className="locationName">{name}</span>
                                {item.type && id?.toLowerCase() !== "location" && (
                                  <span className="locationTypeBadge" style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: '#f0f2f5', color: '#666' }}>{item.type}</span>
                                )}
                              </div>
                            ) : isIndustryLike ? (
                              industryLabel(itemId || name)
                            ) : (
                              name
                            )
                          }
                          nameAsHTML={id === "companyDomain" || id === "location"}
                          id={itemId}
                          checked={checked}
                          cbk={handleSelectAction}
                        />
                      </div>
                    );
                  })
                    : canShowAddCustomItem && (
                      <div
                        className="addCustomItem"
                        onClick={handleAddCustomItem}
                      >
                        Add <span className="customName">{searchValue}</span>
                      </div>
                    )}
                </div>
                {controlApplyBtnVisiblity() && (
                  <Button
                    id="applySelection"
                    name="applySelection"
                    type="button"
                    className="applySelectionBtn"
                    btnText="Apply"
                    click={() => handleApplySelectionButton()}
                  />
                )}
              </div>
            )}
            <div
              className="dropdownOverlay"
              style={disableOverlayClick ? { pointerEvents: "none" } : undefined}
              onClick={() => {
                if (!disableOverlayClick) resetState();
              }}
            />
          </>
        )}
      </div>
    </>
  );
});

export { AutoCompleteMultiSelectDropdown };
export default AutoCompleteMultiSelectDropdown;
